import {
  CrewUi,
  uploadContentTypeMimeTypes,
  uploadContentTypesFileNoun,
} from 'corso-types';
import { z } from 'zod';
import { zfd } from 'zod-form-data';
import {
  DraftClaimLineItem,
  draftClaimLineItemContext,
} from '~/app/features/crew/claim-line-items/claimLineItemMiddleware';
import { crewClaimContext } from '~/app/features/crew/crewClaimMiddleware';
import { settingsContext } from '~/app/settingsMiddleware';
import { ClaimType } from '~/utils/constants';
import { makeAction, makeLoader, redirect } from '~/utils/routing';
import {
  draftClaimLineItemSchema,
  mediaAssetSchema,
  stringToJSONSchema,
} from '~/utils/schemas';

// the SPA can deploy ahead of the API, so apply the schema default to events sent without it
const getAllowedContentTypes = ({ reasonCustomFields }: DraftClaimLineItem) =>
  CrewUi.collectCustomFieldEventSchema.shape.params.shape.allowedContentTypes.parse(
    reasonCustomFields?.params.allowedContentTypes,
  );

export const { loader, useLoaderData } = makeLoader(({ context }) => {
  const { storefrontId, isClaimImageFromCustomerRequired } =
    context.get(settingsContext);
  const { claimType } = context.get(crewClaimContext);
  const draftClaimLineItem = context.get(draftClaimLineItemContext);

  const { reasonCustomFields } = draftClaimLineItem;

  const requireAssets =
    reasonCustomFields?.params.isMediaUploadRequired ??
    (isClaimImageFromCustomerRequired || claimType === ClaimType.warranty);

  // if require assets is true,
  // then the minimumMediaUploadAmount is the provided value or 1
  // if require assets is false, then the minimumMediaUploadAmount is 0
  const requiredMediaCount = requireAssets
    ? (reasonCustomFields?.params.minimumMediaUploadAmount ?? 1)
    : 0;

  const mediaAcceptTypes = getAllowedContentTypes(draftClaimLineItem).flatMap(
    (contentType) => uploadContentTypeMimeTypes[contentType],
  );

  return {
    customFields: reasonCustomFields?.params.customFields ?? [],
    mediaUploadInstructions: reasonCustomFields?.params.mediaUploadInstructions,
    mediaAcceptTypes,
    // assets uploaded under a previously selected reason may not be accepted by this one
    assets: draftClaimLineItem.assets?.filter(({ type }) =>
      mediaAcceptTypes.some((acceptType) => acceptType === type),
    ),
    requiredMediaCount,
    showDefaultCommentField:
      reasonCustomFields?.params.showDefaultCommentField ?? true,
    claim: draftClaimLineItem,
    isDefaultCommentFieldRequired: reasonCustomFields?.params
      .showDefaultCommentField
      ? Boolean(reasonCustomFields.params.isDefaultCommentFieldRequired)
      : false,
    uploadFileInfo: {
      storefrontId,
      destination: 'crewClaimImg' as const,
    },
  };
});

export enum FormKeys {
  Comments = 'comments',
  Assets = 'assets',
  RequiredAssetCount = 'requiredAssetCount',
}

const schema = zfd.formData(
  z
    .object({
      [FormKeys.Comments]: zfd
        .text(z.string().optional())
        .transform((value) => value ?? null),
      [FormKeys.RequiredAssetCount]: zfd.numeric(
        z.number().int().nonnegative(),
      ),
      [FormKeys.Assets]: zfd.repeatableOfType(
        stringToJSONSchema.pipe(mediaAssetSchema),
      ),
    })
    .catchall(draftClaimLineItemSchema.shape.customFields.valueType),
);

export const { action, useActionResult } = makeAction(
  ({ formData, context }, appStore) => {
    // we don't need the formId in our action as it will be caught with the custom fields in catchAll
    formData.delete('formId');
    const results = schema.safeParse(formData);

    if (!results.success) {
      const formInfo = Object.fromEntries(formData.entries()) as Record<
        string,
        unknown
      >;
      throw new Error('Malformed form data', {
        cause: {
          error: results.error,
          formInfo,
        },
      });
    }

    const {
      [FormKeys.Comments]: comments,
      [FormKeys.Assets]: assets,
      files,
      [FormKeys.RequiredAssetCount]: requiredAssetCount,
      ...customFields
    } = results.data;

    const assetsRequired = requiredAssetCount > 0;

    if (assetsRequired && assets.length < requiredAssetCount) {
      return {
        ok: false,
        message: `A minimum of ${requiredAssetCount} ${uploadContentTypesFileNoun(getAllowedContentTypes(context.get(draftClaimLineItemContext)))}${requiredAssetCount === 1 ? ' is' : 's are'} required.`,
      };
    }

    appStore.dispatch(
      appStore.actions.draftClaimLineItem.setMeta({
        customFields,
        comments,
        assets,
      }),
    );

    return redirect('../resolution');
  },
);
